import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon, MinusIcon } from '@/components/icons';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnResponse, SelectedAddOn } from '@/features/cart/types';


interface EditAddOnGuestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  addOn: AddOnResponse | null;
  guestsWithAddOn: number[];
  allGuests: number; // Total number of guests
  pricePerGuest: number;
  totalPrice: number;
  removedGuests?: Set<number>; // Guest indices that are removed
  removedAddOns?: Set<number>; // Guest indices that have this add-on removed
  onRemoveGuest: (guestIndex: number) => void;
  onRestoreGuest?: (guestIndex: number) => void;
  onRestoreAddOn?: (guestIndex: number) => void;
  // Props for quantity editing
  editAddOnsPerGuest?: SelectedAddOn[][];
  originalAddOnsPerGuest?: SelectedAddOn[][]; // Original quantities from cart item (max limit)
  onQuantityChange?: (guestIndex: number, addOnId: string, newQuantity: number) => void;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

const EditAddOnGuestsModal: React.FC<EditAddOnGuestsModalProps> = ({
  isOpen,
  onClose,
  addOn,
  guestsWithAddOn,
  allGuests,
  pricePerGuest,
  removedGuests = new Set(),
  removedAddOns = new Set(),
  onRemoveGuest,
  onRestoreGuest,
  onRestoreAddOn,
  editAddOnsPerGuest,
  originalAddOnsPerGuest,
  onQuantityChange,
  currency
}) => {
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  if (!isOpen || !addOn) return null;

  const addOnId = addOn.id;
  const allowMultiple = addOn.allow_multiple_per_guest;
  const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;

  // Get quantity for a specific guest (current edit state)
  const getGuestQuantity = (guestIndex: number): number => {
    if (!editAddOnsPerGuest || !editAddOnsPerGuest[guestIndex]) return 0;
    const guestAddOn = editAddOnsPerGuest[guestIndex].find(ao => ao.add_on_id === addOnId);
    return guestAddOn?.quantity || 0;
  };

  // Get original quantity for a specific guest (max limit for undo)
  const getOriginalGuestQuantity = (guestIndex: number): number => {
    if (!originalAddOnsPerGuest || !originalAddOnsPerGuest[guestIndex]) return 0;
    const guestAddOn = originalAddOnsPerGuest[guestIndex].find(ao => ao.add_on_id === addOnId);
    return guestAddOn?.quantity || 0;
  };

  // Calculate total quantity across all guests
  const getTotalQuantity = (): number => {
    if (!editAddOnsPerGuest) return guestsWithAddOn.length;
    let total = 0;
    editAddOnsPerGuest.forEach((guestAddOns, idx) => {
      if (removedGuests.has(idx) || removedAddOns.has(idx)) return;
      const guestAddOn = guestAddOns?.find(ao => ao.add_on_id === addOnId);
      if (guestAddOn) {
        total += guestAddOn.quantity || 1;
      }
    });
    return total;
  };

  const totalQuantity = getTotalQuantity();

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md max-h-[90vh] bg-gray-900 rounded-2xl border border-gray-700/50 overflow-hidden shadow-2xl flex flex-col"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-300 hover:text-white transition-all duration-200 backdrop-blur-sm"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {/* Image Header */}
            <div className="relative h-48 overflow-hidden flex-shrink-0">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: imageUrl
                    ? `url(${imageUrl})`
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              
              {/* Name and Price Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-2xl font-bold text-white mb-2">{addOn.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">{formatPrice(pricePerGuest)} per item</span>
                  <span className="text-xl font-bold text-blue-400">
                    {formatPrice(totalQuantity * pricePerGuest)}
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Guests Section */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {allowMultiple ? 'Quantity per Guest' : 'Guests'} ({allGuests})
                </h4>
                
                <div className="space-y-2">
                  {Array.from({ length: allGuests }, (_, i) => i + 1).map((guestNum) => {
                    const guestIndex = guestNum - 1; // Convert to 0-based index
                    const hasAddOn = guestsWithAddOn.includes(guestNum);
                    const isGuestRemoved = removedGuests.has(guestIndex);
                    const isAddOnRemoved = removedAddOns.has(guestIndex);
                    const quantity = getGuestQuantity(guestIndex);
                    
                    return (
                      <div
                        key={guestNum}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          isGuestRemoved
                            ? 'bg-gray-800/20 border-red-500/30 opacity-50'
                            : isAddOnRemoved
                            ? 'bg-gray-800/30 border-orange-500/30 opacity-60'
                            : hasAddOn
                            ? 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800/70'
                            : 'bg-gray-800/30 border-gray-700/30 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                            isGuestRemoved
                              ? 'bg-red-500/20 border-red-500/30'
                              : isAddOnRemoved
                              ? 'bg-orange-500/20 border-orange-500/30'
                              : hasAddOn
                              ? 'bg-blue-500/20 border-blue-500/30'
                              : 'bg-gray-500/20 border-gray-500/30'
                          }`}>
                            <span className={`font-semibold text-sm ${
                              isGuestRemoved
                                ? 'text-red-400'
                                : isAddOnRemoved
                                ? 'text-orange-400'
                                : hasAddOn
                                ? 'text-blue-400'
                                : 'text-gray-400'
                            }`}>
                              {guestNum}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className={`font-medium ${
                              isGuestRemoved
                                ? 'text-red-300 line-through'
                                : isAddOnRemoved
                                ? 'text-orange-300'
                                : hasAddOn
                                ? 'text-white'
                                : 'text-gray-400'
                            }`}>
                              Guest {guestNum}
                            </span>
                            {quantity > 0 && !isGuestRemoved && !isAddOnRemoved && (
                              <span className="text-xs text-blue-400">{formatPrice(pricePerGuest * quantity)}</span>
                            )}
                            {isGuestRemoved && (
                              <span className="text-xs text-red-300">Guest Removed</span>
                            )}
                            {isAddOnRemoved && !isGuestRemoved && (
                              <span className="text-xs text-orange-300">Add-on Removed</span>
                            )}
                            {!hasAddOn && !isGuestRemoved && !isAddOnRemoved && (
                              <span className="text-xs text-gray-500">(No add-on)</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {isGuestRemoved && onRestoreGuest && (
                            <button
                              onClick={() => onRestoreGuest(guestIndex)}
                              className="p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors"
                              title="Restore guest"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                            </button>
                          )}
                          {isAddOnRemoved && !isGuestRemoved && onRestoreAddOn && (
                            <button
                              onClick={() => onRestoreAddOn(guestIndex)}
                              className="p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors"
                              title="Restore add-on"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Quantity Stepper for multiple addons */}
                          {hasAddOn && !isGuestRemoved && !isAddOnRemoved && allowMultiple && onQuantityChange && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => onQuantityChange(guestIndex, addOnId, quantity - 1)}
                                disabled={quantity <= 0}
                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                  quantity <= 0
                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-gray-700 text-white hover:bg-gray-600'
                                }`}
                                title={quantity <= 0 ? 'Already at zero' : 'Decrease quantity'}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                              </button>
                              <span className="w-8 text-center font-semibold text-white">{quantity}</span>
                              <button
                                onClick={() => onQuantityChange(guestIndex, addOnId, quantity + 1)}
                                disabled={quantity >= getOriginalGuestQuantity(guestIndex)}
                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                  quantity >= getOriginalGuestQuantity(guestIndex)
                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                                title={quantity >= getOriginalGuestQuantity(guestIndex) ? 'At original quantity' : 'Increase quantity'}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </div>
                          )}
                          
                          {/* Remove button for single quantity addons */}
                          {hasAddOn && !isGuestRemoved && !isAddOnRemoved && !allowMultiple && (
                            <button
                              onClick={() => onRemoveGuest(guestIndex)}
                              className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
                              title="Remove from guest"
                            >
                              <MinusIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info */}
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-gray-300">
                  {allowMultiple 
                    ? 'Use + and - buttons to adjust quantity per guest. Changes will be saved when you click "Update".'
                    : 'Click the minus button to remove this add-on from a guest. Changes will be saved when you click "Update".'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default EditAddOnGuestsModal;
